import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Target, Eye, Heart, Briefcase } from 'lucide-react';

export default function About() {
  const [showDialog, setShowDialog] = useState(false);

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To empower businesses of all sizes by providing accessible, reliable, and professional trademark registration services that protect their most valuable assets.',
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description: 'To be the most trusted name in intellectual property protection, making trademark registration simple and stress-free for every business owner.',
    },
    {
      icon: Heart,
      title: 'Our Values',
      description: 'Integrity, transparency, and client success drive everything we do. We believe in building lasting relationships based on trust and exceptional service.',
    },
    {
      icon: Briefcase,
      title: 'Our Approach',
      description: 'We combine legal expertise with personalized service, ensuring each client receives tailored solutions that meet their unique needs and goals.',
    },
  ];

  const teamImages = [
    'https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&q=85',
    'https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&q=85',
    'https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=85',
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navigation />

      {/* Hero Section with Background Image */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="about-hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/90 via-[#06214c]/80 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="about-hero-title"
            >
              About IP Protection Associate
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto" data-testid="about-hero-subtitle">
              Your trusted partner in protecting intellectual property and building lasting brand legacies.
            </p>
          </div>
        </div>
      </section>


      {/* Story Section with Images */}
      <section className="section section-abstract-bg bg-[#FFF5E6]" data-testid="about-story-section">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-8" style={{ fontFamily: 'Playfair Display' }}>
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-[#2C1810] leading-relaxed">
                <p>
                  At IP Protection Associate, we understand that your brand is more than just a name or logo—it's the heart and soul of your business. It represents your vision, your hard work, and your promise to your customers. That's why we're dedicated to providing comprehensive trademark registration services that protect what matters most to you.
                </p>
                <p>
                  Our journey began with a simple goal: to make intellectual property protection accessible to businesses of all sizes. We recognized that navigating the complex world of trademark law could be overwhelming, especially for small business owners and entrepreneurs. We set out to change that by creating a streamlined process that combines expert legal knowledge with personalized, attentive service.
                </p>
                <p>
                  Over the years, we've helped thousands of businesses secure their trademarks, from startups launching their first product to established companies expanding their brand portfolio. Each success story reinforces our commitment to excellence and drives us to continuously improve our services.
                </p>

                <button
                onClick={() => setShowDialog(true)}
                className="btn-primary text-center text-xl py-3 px-10 mt-auto max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Get Started Now
              </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 h-64 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={teamImages[0]} 
                  alt="Our team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="h-48 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={teamImages[1]} 
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="h-48 rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={teamImages[2]} 
                  alt="Team success"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      
      {/* Values Section */}
      <section className="section section-abstract-shapes bg-[#FFF5E6]" data-testid="about-values-section">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              What Drives Us
            </h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">
              Our core principles guide everything we do and define who we are.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-[664px]:flex max-[664px]:gap-6 max-[664px]:overflow-x-auto max-[664px]:snap-x max-[664px]:scroll-smooth max-[664px]:px-8 max-[664px]:-mx-8 max-[664px]:pb-4">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="card fade-in max-[664px]:flex-shrink-0 max-[664px]:w-[280px] max-[664px]:snap-start" data-testid={`value-${index}`}>
                  <div className="flex items-start space-x-4 max-[664px]:flex-col max-[664px]:items-center max-[664px]:text-center">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                        <Icon className="text-white" size={32} />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Commitment Section with Background */}
      <section 
        className="section relative overflow-hidden" 
        data-testid="about-commitment-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1606857521015-7f9fcf423740?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#06214c]/95 to-[#A0522D]/95"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Playfair Display' }}>
            Our Commitment to You
          </h2>
          <p className="text-lg mb-8 max-w-3xl mx-auto text-white/90 leading-relaxed">
            We're more than just a service provider—we're your partner in building and protecting your brand.
            Every trademark we register represents a business owner's dream, and we take that responsibility seriously.
            With expert guidance, transparent communication, and unwavering support, we ensure your brand is protected
            so you can focus on what you do best: growing your business.
          </p>

          <button
                onClick={() => setShowDialog(true)}
                className="bg-white text-[#06214c] border-2 border-[#ffffff] px-10 py-3 rounded-full font-bold text-xl hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-2xl max-[360px]:text-sm max-[360px]:px-5 max-[360px]:py-2"
              >
                Get Started Now
              </button>
        </div>
      </section>

      <ContactFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        source="about"
      />

      <Footer />
    </div>
  );
}